import React, { useState, useEffect, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../../styles/ag-grid-custom.css";
import { ColDef, ModuleRegistry } from "ag-grid-community";
import { CellStyleModule, PaginationModule } from "ag-grid-community";
import { Search, Users, FileText, Download } from "lucide-react";
import { toast } from "react-toastify";
import { formatDate } from "../../IncidentManagement/utils/utils";
import {
  fetchICSIncidents,
  getICSIncidentById,
  updateICSIncident,
  getICSRoles,
  getDecisionLogs,
} from "./services/service";
import ActionModal from "../../IncidentManagement/components/ActionModal";
import { ICSIncident, ICSStatus } from "./types/types";
import ICSIncidentDetails from "./components/ICSIncidentDetails";
import {
  fetchIncidentsByDepartment,
  getIncidentById,
  updateIncident,
} from "../../IncidentManagement/services/service";
import { Incident } from "../../IncidentManagement/types/types";
import { usePermissions } from "../../../hooks/usePermission";
import { EXTERNAL_EMERGENCY_PREPAREDNESS_ICS } from "../../../constants/modules";
import axiosInstance from "../../../common/axiosInstance";

ModuleRegistry.registerModules([CellStyleModule, PaginationModule]);

const ICSIncidents: React.FC = () => {
  const [icsIncidents, setICSIncidents] = useState<ICSIncident[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<ICSStatus | "">("");
  const [selectedIncident, setSelectedIncident] = useState<ICSIncident | null>(
    null,
  );
  const [showDetails, setShowDetails] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  const [pageSize] = useState(10);
  const gridRef = useRef<any>(null);
  const { canUpdate, canRead } = usePermissions();

  const fetchICSData = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {};

      if (statusFilter) {
        params.status = statusFilter;
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      // Fetch ICS incidents
      const icsResponse = await fetchICSIncidents(params);
      let allIncidents: ICSIncident[] = [];

      if (icsResponse.success) {
        allIncidents = icsResponse.data || [];
      }

      // Also fetch External Emergency Preparedness incidents that haven't been converted to ICS yet
      try {
        const externalEmergencyResponse = await fetchIncidentsByDepartment(
          "External Emergency Preparedness",
        );
        if (externalEmergencyResponse.success) {
          const externalIncidents: Incident[] =
            externalEmergencyResponse.data || [];

          // Get existing ICS incident IDs to avoid duplicates
          const existingIncidentIds = new Set(
            allIncidents.map((ics) => ics.incidentId),
          );

          // Convert regular External Emergency incidents to ICSIncident format
          // Map incident status to ICS status
          const mapIncidentStatusToICS = (status: string): ICSStatus => {
            switch (status) {
              case "Open":
                return "Active";
              case "Pending":
                return "Standby";
              case "Resolved":
                return "Completed";
              default:
                return "Standby";
            }
          };

          const convertedIncidents: ICSIncident[] = externalIncidents
            .filter((incident) => !existingIncidentIds.has(incident.id))
            .map((incident) => ({
              id: `regular-${incident.id}`, // Mark as regular incident (not ICS yet)
              incidentId: incident.id,
              incidentNumber: incident.incidentNumber,
              status: mapIncidentStatusToICS(
                incident.status || "Open",
              ) as ICSStatus,
              activatedAt:
                incident.reportDate ||
                incident.incidentDate ||
                new Date().toISOString(),
              activatedBy: incident.reportedBy || "",
              activatedByName: incident.reportedBy || "",
              department:
                incident.department || "External Emergency Preparedness",
              roles: [],
              decisionLogs: [],
              notes: "",
            }));

          // Enrich regular incidents with roles and decision logs from API (so table shows correct counts)
          const enrichedConverted = await Promise.all(
            convertedIncidents.map(async (inc) => {
              try {
                const [rolesRes, decisionsRes] = await Promise.all([
                  getICSRoles(inc.id),
                  getDecisionLogs(inc.id),
                ]);
                return {
                  ...inc,
                  roles: rolesRes.data ?? [],
                  decisionLogs: decisionsRes.data ?? [],
                };
              } catch {
                return inc;
              }
            }),
          );

          // Combine ICS incidents and pending External Emergency incidents
          allIncidents = [...allIncidents, ...enrichedConverted];
        }
      } catch (error) {
        console.warn("Failed to fetch External Emergency incidents:", error);
        // Continue with ICS incidents only if External Emergency fetch fails
      }

      // Apply search filter if provided
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        allIncidents = allIncidents.filter(
          (incident) =>
            incident.incidentNumber?.toLowerCase().includes(searchLower) ||
            incident.department?.toLowerCase().includes(searchLower),
        );
      }

      // Apply status filter if provided
      if (statusFilter) {
        allIncidents = allIncidents.filter(
          (incident) => incident.status === statusFilter,
        );
      }

      setICSIncidents(allIncidents);
    } catch (error) {
      console.error("Failed to fetch ICS incidents:", error);
      toast.error("Failed to load ICS incidents");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  useEffect(() => {
    fetchICSData();
  }, [fetchICSData]);

  const handleViewDetails = async (incident: ICSIncident) => {
    try {
      // Check if this is a regular incident (not yet converted to ICS)
      if (incident.id.startsWith("regular-")) {
        // Fetch the regular incident and convert it to ICS format
        const regularIncidentId = incident.incidentId;
        const regularIncidentResponse =
          await getIncidentById(regularIncidentId);

        if (regularIncidentResponse.success) {
          const regularIncident: Incident = regularIncidentResponse.data;

          // Fetch roles and decision logs for this incident (stored under "regular-<id>" in backend)
          const [rolesRes, decisionLogsRes] = await Promise.all([
            getICSRoles(incident.id),
            getDecisionLogs(incident.id),
          ]);

          const icsIncident: ICSIncident = {
            id: `regular-${regularIncident.id}`,
            incidentId: regularIncident.id,
            incidentNumber: regularIncident.incidentNumber,
            status: incident.status,
            activatedAt:
              regularIncident.reportDate ||
              regularIncident.incidentDate ||
              new Date().toISOString(),
            activatedBy: regularIncident.reportedBy || "",
            activatedByName: regularIncident.reportedBy || "",
            department:
              regularIncident.department || "External Emergency Preparedness",
            roles: rolesRes.data ?? [],
            decisionLogs: decisionLogsRes.data ?? [],
            notes: "",
          };

          setSelectedIncident(icsIncident);
          setShowDetails(true);
        } else {
          toast.error("Failed to load incident details");
        }
        return;
      }

      // Regular ICS incident - fetch normally
      const response = await getICSIncidentById(incident.id);
      if (response.success) {
        setSelectedIncident(response.data);
        setShowDetails(true);
      }
    } catch (error) {
      toast.error("Failed to load incident details");
    }
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedIncident(null);
    fetchICSData();
  };

  const handleStatusChange = async (
    incidentId: string,
    newStatus: ICSStatus,
  ) => {
    try {
      // Check if this is a regular incident (not yet converted to ICS)
      if (incidentId.startsWith("regular-")) {
        // Extract the actual incident ID
        const actualIncidentId = incidentId.replace("regular-", "");

        // Map ICS status back to regular incident status
        const mapICSStatusToIncidentStatus = (icsStatus: ICSStatus): string => {
          switch (icsStatus) {
            case "Active":
              return "Open";
            case "Standby":
              return "Pending";
            case "Completed":
              return "Resolved";
            case "Cancelled":
              return "Rejected";
            default:
              return "Open";
          }
        };

        const incidentStatus = mapICSStatusToIncidentStatus(newStatus);

        // Update the regular incident status
        const response = await updateIncident(actualIncidentId, {
          status: incidentStatus,
        });

        if (response.success) {
          toast.success("Status updated successfully");
          fetchICSData();
        } else {
          toast.error("Failed to update status");
        }
        return;
      }

      // Regular ICS incident - update normally
      const response = await updateICSIncident(incidentId, {
        status: newStatus,
      });
      if (response.success) {
        toast.success("Status updated successfully");
        fetchICSData();
      }
    } catch (error) {
      toast.error("Failed to update status");
      console.error("Status update error:", error);
    }
  };

  const handleBulkDownload = async () => {
    try {
      setLoading(true);

      // Build query params
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (searchTerm) params.append("search", searchTerm);

      // Call the same bulk export endpoint with ICS-specific parameters
      const response = await axiosInstance.get(
        `/api/ics/incidents/export/bulk`,
        {
          params: Object.fromEntries(params),
          responseType: "blob",
        },
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `ICS_Incidents_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Download started successfully");
    } catch (error) {
      console.error("Bulk download failed:", error);
      toast.error("Failed to download ICS incidents");
    } finally {
      setLoading(false);
    }
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "Incident Number",
      field: "incidentNumber",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
    },
    {
      headerName: "Department",
      field: "department",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      valueFormatter: (params) => params.value ?? "—",
    },
    {
      headerName: "Status",
      field: "status",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      cellRenderer: (params: any) => {
        const status = params.value;
        const getStatusStyle = (status: string) => {
          let color = "#6b7280";
          let bgColor = "#f3f4f6";
          switch (status) {
            case "Active":
              color = "#dc2626";
              bgColor = "#fee2e2";
              break;
            case "Standby":
              color = "#ea580c";
              bgColor = "#fed7aa";
              break;
            case "Completed":
              color = "#16a34a";
              bgColor = "#dcfce7";
              break;
            case "Cancelled":
              color = "#6b7280";
              bgColor = "#f3f4f6";
              break;
          }
          return { color, backgroundColor: bgColor };
        };

        const style = getStatusStyle(status);

        return (
          <select
            value={status}
            onChange={(e) =>
              handleStatusChange(params.data.id, e.target.value as ICSStatus)
            }
            disabled={!canUpdate(EXTERNAL_EMERGENCY_PREPAREDNESS_ICS)}
            style={{
              ...style,
              fontWeight: "600",
              padding: "6px 12px",
              borderRadius: "4px",
              border: `1px solid ${style.color}`,
              cursor: canUpdate(EXTERNAL_EMERGENCY_PREPAREDNESS_ICS)
                ? "pointer"
                : "not-allowed",
              width: "100%",
              textAlign: "center",
              opacity: canUpdate(EXTERNAL_EMERGENCY_PREPAREDNESS_ICS) ? 1 : 0.6,
            }}
            className="focus:outline-none"
          >
            <option value="Active">Active</option>
            <option value="Standby">Standby</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        );
      },
    },
    {
      headerName: "Activated At",
      field: "activatedAt",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Activated By",
      field: "activatedByName",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
    },
    {
      headerName: "Roles Assigned",
      field: "roles",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      valueGetter: (params) => {
        return params.data?.roles?.length || 0;
      },
      cellRenderer: (params: any) => {
        const count = params.data?.roles?.length || 0;
        return (
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-600" />
            <span>{count} roles</span>
          </div>
        );
      },
    },
    {
      headerName: "Decisions",
      field: "decisionLogs",
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      valueGetter: (params) => {
        return params.data?.decisionLogs?.length || 0;
      },
      cellRenderer: (params: any) => {
        const count = params.data?.decisionLogs?.length || 0;
        return (
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-green-600" />
            <span>{count} decisions</span>
          </div>
        );
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      headerStyle: { backgroundColor: "#e9f5ff", textAlign: "center" },
      cellRenderer: (params: any) => {
        return (
          <div className="flex items-center gap-2 justify-center">
            <button
              className="px-4 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 text-sm font-medium"
              onClick={() => handleViewDetails(params.data)}
            >
              View Details
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="mt-16 px-6 space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            {/* <AlertCircle className="w-8 h-8 text-red-600" /> */}
            <h1 className="text-2xl font-semibold">
              Incident Command System (ICS)
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {/* {canRead(EXTERNAL_EMERGENCY_PREPAREDNESS_ICS) && (
               <button
                  className="flex items-center justify-center font-semibold gap-3 bg-[#4f5762] text-white px-4 py-2 rounded-full"
                  onClick={() => {
                    setIsDownload(true);
                  }}
               > */}
            {/* For ICS, we'll just reuse the text icon since we don't have images exported directly here unless we import them */}
            {/* <FileText className="w-5 h-5 text-white" />
                 BULK EXPORT
               </button> */}
            {/* )} */}
          </div>
        </div>

        <div className="bg-white w-full h-full drop-shadow-[0_0px_10px_rgba(0,0,0,0.25)]">
          <div className="p-6">
            <div className="flex justify-between items-center">
              <div className="flex gap-5 items-center">
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search by Incident Number"
                    className="border-2 border-[#4f5762] w-[300px] px-10 py-2 rounded placeholder:font-semibold placeholder:text-[#4e545d]"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <select
                  name="status"
                  id="status"
                  className="border-2 border-[#4f5762] w-[250px] px-3 py-2 rounded font-semibold text-[#4e545d]"
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as ICSStatus | "")
                  }
                >
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Standby">Standby</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              <button
                type="button"
                onClick={handleBulkDownload}
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded font-semibold hover:bg-teal-700"
              >
                <Download size={18} />
                Bulk Download
              </button>
            </div>
          </div>

          <div className="ag-theme-alpine">
            {loading ? (
              <div className="p-4 text-center">Loading ICS incidents...</div>
            ) : (
              <AgGridReact
                ref={gridRef}
                rowData={icsIncidents}
                columnDefs={columnDefs}
                rowHeight={50}
                headerHeight={65}
                paginationPageSize={pageSize}
                pagination={true}
                domLayout="autoHeight"
                defaultColDef={{
                  sortable: true,
                  resizable: true,
                }}
                getRowStyle={() => ({
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  marginBottom: "8px",
                  borderRadius: "4px",
                  backgroundColor: "white",
                })}
              />
            )}
          </div>
        </div>
      </div>

      {showDetails && selectedIncident && (
        <ICSIncidentDetails
          incident={selectedIncident}
          onClose={handleCloseDetails}
        />
      )}

      {isDownload && (
        <ActionModal
          showModal={false}
          showRCAModal={false}
          showDownload={isDownload}
          setShowRCAModal={() => {}}
          setShowModal={() => {}}
          setShowDownload={setIsDownload}
          data={null}
          setSelectedRowData={() => {}}
          onActionComplete={() => {}}
          mode="view"
          actionId={undefined}
          setRcaId={() => {}}
          rcaId={null}
          moduleKey={EXTERNAL_EMERGENCY_PREPAREDNESS_ICS}
          department="External Emergency Preparedness"
        />
      )}
    </>
  );
};

export default ICSIncidents;
